/**
 * axios adapter that falls back to fetch when the page's XMLHttpRequest is broken.
 *
 * Some Android in-app browsers (Samsung WebView, mostly local-jobs.org traffic) inject a script
 * that replaces XMLHttpRequest.open with a wrapper calling a native bridge, XHRInterface, which
 * the app never exposes on our pages. open() then throws "ReferenceError: XHRInterface is not
 * defined" before any request leaves, so /api/getjobs never runs and the SERP renders empty.
 *
 * axios surfaces that as a plain error, not an AxiosError: a failed request always carries
 * isAxiosError, a throw from the environment does not. That is the only case retried over fetch.
 * Once it happens, XHR is skipped for the rest of the page.
 *
 * axios 1.6 has no fetch adapter of its own (1.7 does), hence the small one below. It runs after
 * transformRequest and before transformResponse, like the xhr adapter it stands in for, so the
 * CSRF retry interceptor in bootstrap.js and every caller see an ordinary axios response.
 */
export default function installXhrFallback(axios) {
    const xhrAdapter = axios.getAdapter('xhr');
    let xhrBroken = false;

    function fetchAdapter(config) {
        const url = axios.getUri(config);
        const headers = {};
        Object.entries(config.headers && config.headers.toJSON ? config.headers.toJSON() : (config.headers || {}))
            .forEach(([k, v]) => { if (v !== undefined && v !== null && v !== false) headers[k] = String(v); });

        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        let timedOut = false;
        const timer = config.timeout && controller
            ? setTimeout(() => { timedOut = true; controller.abort(); }, config.timeout)
            : null;
        if (config.signal && controller) {
            if (config.signal.aborted) controller.abort();
            else config.signal.addEventListener('abort', () => controller.abort());
        }

        const method = (config.method || 'get').toUpperCase();
        return fetch(url, {
            method,
            headers,
            body: method === 'GET' || method === 'HEAD' ? undefined : config.data,
            credentials: config.withCredentials ? 'include' : 'same-origin',
            signal: controller ? controller.signal : undefined,
        }).then(res => {
            clearTimeout(timer);
            return (config.responseType === 'blob' ? res.blob() : res.text()).then(data => {
                const response = {
                    data,
                    status: res.status,
                    statusText: res.statusText,
                    headers: new axios.AxiosHeaders(Object.fromEntries(res.headers.entries())),
                    config,
                    request: null,
                };
                const validate = config.validateStatus;
                if (!res.status || !validate || validate(res.status)) return response;
                throw new axios.AxiosError(
                    'Request failed with status code ' + res.status,
                    res.status >= 500 ? axios.AxiosError.ERR_BAD_RESPONSE : axios.AxiosError.ERR_BAD_REQUEST,
                    config, null, response
                );
            });
        }, err => {
            clearTimeout(timer);
            if (err && err.isAxiosError) throw err;
            if (timedOut) {
                throw new axios.AxiosError('timeout of ' + config.timeout + 'ms exceeded', axios.AxiosError.ECONNABORTED, config, null);
            }
            if (err && err.name === 'AbortError') {
                throw new axios.Cancel('canceled', config);
            }
            throw new axios.AxiosError(err && err.message ? err.message : 'Network Error', axios.AxiosError.ERR_NETWORK, config, null);
        });
    }

    axios.defaults.adapter = function (config) {
        if (xhrBroken) return fetchAdapter(config);
        if (typeof fetch === 'undefined') return xhrAdapter(config);
        return xhrAdapter(config).catch(err => {
            if (err && err.isAxiosError) throw err;
            if (axios.isCancel && axios.isCancel(err)) throw err;
            xhrBroken = true;
            if (typeof Sentry !== 'undefined' && Sentry.addBreadcrumb) {
                Sentry.addBreadcrumb({ category: 'xhr', level: 'warning', message: 'XHR broken, using fetch: ' + (err && err.message) });
            }
            return fetchAdapter(config);
        });
    };
}
